import * as React from 'react';
import { Button, Typography } from '@/components/common';
import Icons from '@/const/icons';
import { ModalHeader } from '@/components/dashboard';
import { useRouter } from 'next/router';
import useModal from '@/hooks/useModal';
import { SaveStatus } from '../SaveStatus';
import AppRoutes from '@/const/routes';
import { CaseContext } from '@/context/CaseContext';
import { auditService, GET_LAWYER_CASE_DETAILS } from '@/api';
import { useCaseAdministration, useCaseStep, useNotification } from '@/hooks';
import { IntakeStatus } from './IntakeStatus';
import { useQueryClient } from '@tanstack/react-query';
import { getValidCaseId } from '@/utils';

export const ApplicantIntakeActions = () => {
	const router = useRouter();
	const caseId = getValidCaseId(router.query.id);
	const id = caseId;
	const { toastError, toastSuccess } = useNotification();
	const { handleMoveToTheNextPhase } = useCaseAdministration({
		id: caseId as string,
	});

	const queryClient = useQueryClient();
	const { caseDetails } = React.useContext(CaseContext);
	const { isPhaseCompleted } = useCaseStep();
	const { Modal, show, hide } = useModal();
	const [showModal, setShowModal] = React.useState(false);

	const isCompleted = isPhaseCompleted('Applicant Intake');

	const refetchQueries = () => {
		if (!caseId) return;
		queryClient.invalidateQueries({
			queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
		});
	};

	const handlePublishCase = async () => {
		try {
			if (id) {
				await auditService.patchGoLive(id as string);
				show();
				handleMoveToTheNextPhase();
			}
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		}
	};

	const handleContinue = async (status: boolean) => {
		try {
			await auditService.patchIntakeStatus(id as string, status);
			toastSuccess('The case has been successfully published');
			refetchQueries();
			movePhase();
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		}
	};

	const onClickMoveToNextPhase = () => {
		if (!caseDetails?.intake_status_changed_at) {
			setShowModal(true);
		} else {
			movePhase();
		}
	};

	const movePhase = () => {
		router.push(
			{
				pathname: `${AppRoutes.LAWYERAPP_CASES}/${id}/active-case`,
			},
			undefined,
			{ shallow: true }
		);
	};

	const handleClose = () => {
		hide();
	};

	return (
		<>
			<div className="flex justify-between items-center gap-x-6">
				<SaveStatus />
				<div className="flex gap-x-6 items-center">
					<Button
						label="Previous"
						decoration="line-primary"
						size="fit"
						iconLeft
						icon={Icons.arrowLeft}
						href={AppRoutes.LAWYERAPP_CASES + `/${id}/investigation`}
					/>
					{caseDetails?.is_draft ? (
						<Button
							label="Publish Case"
							decoration="primary"
							size="fit"
							onClick={handlePublishCase}
							disabled={!isCompleted}
						/>
					) : (
						<Button
							label="Continue to Next Phase"
							decoration="primary"
							iconRight
							icon={Icons.arrowRight}
							size="fit"
							onClick={onClickMoveToNextPhase}
						/>
					)}
				</div>
			</div>

			<Modal
				title={<ModalHeader title="The case is live" />}
				classNameContainerChildren="max-w-[450px]"
				withXButton
			>
				<Typography type="regular-16" className="text-black text-center">
					{`You can start reaching out to clients immediately. When you're ready
					to stop accepting new applicants, move to the next phase.`}
				</Typography>
				<div className="flex justify-center mt-8">
					<Button
						label="Close"
						decoration="secondary"
						size="fit"
						onClick={handleClose}
					/>
				</div>
			</Modal>
			{showModal && (
				<IntakeStatus
					isOpen={showModal}
					setIsOpen={setShowModal}
					isCasePublished={!caseDetails?.intake_status_changed_at}
					isCaseOpenToClients={false}
					handleContinue={handleContinue}
				/>
			)}
		</>
	);
};
